import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { usePostAccountMutation } from 'api';
import { PostAccountRequest } from 'api/bindings/PostAccountRequest';
import { AppInput, FormButtonGroup } from 'features';
import { useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface CreateAccountDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {}

interface Form extends PostAccountRequest {}

export const CreateAccountDrawer = (props: CreateAccountDrawerProps) => {
  const { ...rest } = props;

  // Fetches
  const [postAccount] = usePostAccountMutation();

  // States
  const toast = useToast();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {},
  });
  const { control, watch, handleSubmit, reset } = form;
  const [accountCode, accountName] = watch(['code', 'name']);

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await postAccount(form);
        if ('error' in result) {
          toast({
            title: `Failed to post account ${form.code} - ${form.name}`,
            description: (result.error as any)?.data?.message,
            status: 'error',
          });
          return;
        }
        toast({
          title: `Account ${form.code} - ${form.name} posted successfully!`,
          status: 'success',
        });
        reset();
        rest.onClose();
      } catch {}
    },
    [postAccount, reset, rest, toast]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer {...rest} size="xl">
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerHeader>
              Creating Account: {accountCode} - {accountName}
            </DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="code">Code:</FormLabel>
                  <Controller
                    control={control}
                    name="code"
                    rules={{
                      required: {
                        value: true,
                        message: `Account code is required.`,
                      },
                      min: {
                        value: 0,
                        message: `Account code must be zero or higher.`,
                      },
                      pattern: {
                        value: /^\d+$/,
                        message: 'Account code must be numeral.',
                      },
                    }}
                    render={(f) => (
                      <AppInput type="number" id="code" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    control={control}
                    name="name"
                    rules={{
                      required: {
                        value: true,
                        message: `Account name is required.`,
                      },
                      minLength: {
                        value: 3,
                        message: `Account name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `Account name must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="description">Description:</FormLabel>
                  <Controller
                    control={control}
                    name="description"
                    rules={{
                      required: {
                        value: true,
                        message: 'Account description is required.',
                      },
                      maxLength: {
                        value: 256,
                        message: `Description must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="description" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onConfirm={handleSubmit(onSubmit)}
                onCancel={() => {
                  reset();
                  rest.onClose();
                }}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
